import { FC, useState } from 'react';
import { Tree } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { useNavigate, useParams } from 'react-router-dom';

import { AddButton } from '../../../../../../lib/components/Buttons/AddButton';
import { ButtonsContainer } from '../../../../../../lib/components/Buttons/ButtonsContainer';
import { RemoveButton } from '../../../../../../lib/components/Buttons/RemoveButton';
import { CellStyle } from '../../../../../../lib/components/DataTable/CellStyle';
import { ColumnFactory } from '../../../../../../lib/components/DataTable/ColumnFactory';
import { DataTable } from '../../../../../../lib/components/DataTable/DataTable';
import { useRequestFeedback } from '../../../../../../lib/hooks';
import { ChapterEntity, ChapterTypes, Languages } from '../../../../../../lib/model/Book';
import { extract_num_id_from_id } from '../../../utils/extract_num_id_from_id';
import { useChaptersApi } from '../hooks/useChaptersApi';
import { CreateChapterModal } from './CreateChapterModal';

interface IProps {
	chapters: ChapterEntity[];
	on_remove_success: () => void;
}

export const ChaptersDataTable: FC<IProps> = ({ chapters, on_remove_success }) => {
	const { id, edition_id } = useParams() as {
		id: string;
		edition_id: string;
		chapter_id: string;
	};

	const book_num_id = extract_num_id_from_id(id);
	const edition_num_id = extract_num_id_from_id(edition_id);

	const BASE_URL = `${process.env.REACT_APP_BOOKS_API}/books/${book_num_id}/editions/${edition_num_id}/chapters`;

	const navigate = useNavigate();
	const [showCreateModal, setShowCreateModal] = useState(false);
	const { remove, insert, get_last_id } = useChaptersApi();

	if (remove.result) {
		on_remove_success();
	}

	useRequestFeedback(remove.result, remove.error);

	const buildTreeData = (items: any[]): DataNode[] => {
		return items.map((item) => ({
			key: item.id ?? item.num_id ?? item.title,
			title: <span>{item.title}</span>,
			children: item.items && item.items.length > 0 ? buildTreeData(item.items) : undefined,
		}));
	};

	const handleCreateChapter = async (title: string, chapterType: ChapterTypes) => {
		try {
			// Se NÃO for do tipo Capítulo, redireciona para a rota 'novo' repassando title, chapterType e order
			if (chapterType !== ChapterTypes.CHAPTER) {
				setShowCreateModal(false);

				// Calcula a nova ordem baseada no tamanho da lista + 1
				const nextOrder = chapters.length + 1;

				navigate('novo', {
					state: {
						initialTitle: title,
						chapterType: chapterType,
						order: nextOrder, // <--- NOVO CAMPO
					},
				});
				return;
			}

			// Se FOR do tipo Capítulo, mantém a criação direta no banco
			const last = await get_last_id.get();
			const lastNumId = Number(String(last.num_id).replace(/ /g, ''));
			const now = new Date();

			const chapter = await insert.create({
				num_id: String(lastNumId + 1),
				title,
				order: chapters.length + 1,
				chapter_type: chapterType,
				language: Languages.PORTUGUESE,
				authors: [],
				createDate: now,
				alterDate: now,
				deleted: false,
			});

			setShowCreateModal(false);

			if (chapter?.id) {
				navigate(chapter.id);
			}
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<>
			<CreateChapterModal
				open={showCreateModal}
				onCancel={() => setShowCreateModal(false)}
				onCreate={handleCreateChapter}
			/>
			<DataTable
				{...{
					rowData: [...chapters].sort((a, b) => a.order - b.order),

					buttons: [
						<AddButton
							{...{
								onClick: () => setShowCreateModal(true),
							}}
						/>,
					],

					columns: [
						ColumnFactory.Link(
							{
								field: 'title',
								headerName: 'Titulo',
								resizable: true,
								flex: 2,
								minWidth: 220,
							},
							(props: { data: { id: string } }) => props.data.id
						),

						{
							col_def: {
								field: 'items_tree',
								headerName: 'Sumário do capítulo',
								resizable: true,
								flex: 3,
								minWidth: 350,
								autoHeight: true,
							},

							renderer: (props: any) => {
								const items = props.data.items;

								if (!items || items.length === 0) {
									return <span>-</span>;
								}

								return (
									<div
										style={{
											paddingTop: 8,
											paddingBottom: 8,
										}}
									>
										<Tree
											treeData={buildTreeData(items)}
											selectable={false}
											showLine
											defaultExpandAll={false}
											blockNode={false}
										/>
									</div>
								);
							},
						},

						{
							field: 'chapter_type',
							headerName: 'Tipo',
							resizable: false,
							flex: 1,
							minWidth: 120,
						},

						{
							field: 'language',
							headerName: 'Idioma',
							resizable: false,
							flex: 1,
							minWidth: 100,
						},

						{
							field: 'authors',
							headerName: 'Autor(es)',
							resizable: true,
							flex: 2,
							minWidth: 240,
						},

						{
							col_def: {
								field: 'buttons',
								headerName: '',
								pinned: 'right',
								cellStyle: CellStyle.Centered,
								width: 180,
							},

							renderer: (props: {
								data: {
									num_id: string;
									title: string;
								};
							}) => (
								<ButtonsContainer>
									<RemoveButton
										{...{
											style: {
												marginLeft: '.5rem',
											},

											on_confirm: () => remove.do_remove(props.data.num_id),

											title: `Deseja remover o livro ${props.data.title}?`,
										}}
									/>
								</ButtonsContainer>
							),
						},
					],
				}}
			/>
		</>
	);
};

// import { FC, useState } from 'react';
// import { Tree } from 'antd';
// import type { DataNode } from 'antd/es/tree';
// import { useNavigate, useParams } from 'react-router-dom';

// import { AddButton } from '../../../../../../lib/components/Buttons/AddButton';
// import { ButtonsContainer } from '../../../../../../lib/components/Buttons/ButtonsContainer';
// import { RemoveButton } from '../../../../../../lib/components/Buttons/RemoveButton';
// import { CellStyle } from '../../../../../../lib/components/DataTable/CellStyle';
// import { ColumnFactory } from '../../../../../../lib/components/DataTable/ColumnFactory';
// import { DataTable } from '../../../../../../lib/components/DataTable/DataTable';
// import { useRequestFeedback } from '../../../../../../lib/hooks';
// import { ChapterEntity, ChapterTypes, Languages } from '../../../../../../lib/model/Book';
// import { extract_num_id_from_id } from '../../../utils/extract_num_id_from_id';
// import { useChaptersApi } from '../hooks/useChaptersApi';
// import { CreateChapterModal } from './CreateChapterModal';

// interface IProps {
// 	chapters: ChapterEntity[];
// 	on_remove_success: () => void;
// }

// export const ChaptersDataTable: FC<IProps> = ({ chapters, on_remove_success }) => {
// 	const { id, edition_id } = useParams() as {
// 		id: string;
// 		edition_id: string;
// 		chapter_id: string;
// 	};

// 	const book_num_id = extract_num_id_from_id(id);
// 	const edition_num_id = extract_num_id_from_id(edition_id);

// 	const BASE_URL = `${process.env.REACT_APP_BOOKS_API}/books/${book_num_id}/editions/${edition_num_id}/chapters`;

// 	const navigate = useNavigate();
// 	const [showCreateModal, setShowCreateModal] = useState(false);
// 	//const { remove } = useChaptersApi();
// 	const { remove, insert, get_last_id } = useChaptersApi();

// 	if (remove.result) {
// 		on_remove_success();
// 	}

// 	useRequestFeedback(remove.result, remove.error);

// 	const buildTreeData = (items: any[]): DataNode[] => {
// 		return items.map((item) => ({
// 			key: item.id ?? item.num_id ?? item.title,

// 			title: <span>{item.title}</span>,

// 			children: item.items && item.items.length > 0 ? buildTreeData(item.items) : undefined,
// 		}));
// 	};

// 	const handleCreateChapter = async (title: string, chapterType: ChapterTypes) => {
// 		try {
// 			const last = await get_last_id.get();
// 			const lastNumId = Number(String(last.num_id).replace(/ /g, ''));
// 			const now = new Date();

// 			const chapter = await insert.create({
// 				num_id: String(lastNumId + 1),
// 				title,
// 				order: chapters.length + 1, // Cálculo da ordem direto aqui
// 				chapter_type: chapterType,
// 				language: Languages.PORTUGUESE,
// 				authors: [],
// 				createDate: now,
// 				alterDate: now,
// 				deleted: false,
// 			});

// 			setShowCreateModal(false);

// 			if (chapter?.id) {
// 				navigate(chapter.id); // Redireciona para o capítulo recém-criado
// 			}
// 		} catch (err) {
// 			console.error(err);
// 		}
// 	};
// 	return (
// 		<>
// 			<CreateChapterModal
// 				open={showCreateModal}
// 				onCancel={() => setShowCreateModal(false)}
// 				onCreate={handleCreateChapter}
// 			/>
// 			<DataTable
// 				{...{
// 					rowData: [...chapters].sort((a, b) => a.order - b.order),

// 					buttons: [
// 						<AddButton
// 							{...{
// 								onClick: () => setShowCreateModal(true),
// 							}}
// 						/>,
// 						// <AddButton
// 						// 	{...{
// 						// 		onClick: createChapter,
// 						// 	}}
// 						// />,
// 					],

// 					columns: [
// 						ColumnFactory.Link(
// 							{
// 								field: 'title',
// 								headerName: 'Titulo',
// 								resizable: true,
// 								flex: 2,
// 								minWidth: 220,
// 							},
// 							(props: { data: { id: string } }) => props.data.id
// 						),

// 						{
// 							col_def: {
// 								field: 'items_tree',
// 								headerName: 'Sumário do capítulo',
// 								resizable: true,
// 								flex: 3,
// 								minWidth: 350,
// 								autoHeight: true,
// 							},

// 							renderer: (props: any) => {
// 								const items = props.data.items;

// 								if (!items || items.length === 0) {
// 									return <span>-</span>;
// 								}

// 								return (
// 									<div
// 										style={{
// 											paddingTop: 8,
// 											paddingBottom: 8,
// 										}}
// 									>
// 										<Tree
// 											treeData={buildTreeData(items)}
// 											selectable={false}
// 											showLine
// 											defaultExpandAll={false}
// 											blockNode={false}
// 										/>
// 									</div>
// 								);
// 							},
// 						},

// 						{
// 							field: 'chapter_type',
// 							headerName: 'Tipo',
// 							resizable: false,
// 							flex: 1,
// 							minWidth: 120,
// 						},

// 						{
// 							field: 'language',
// 							headerName: 'Idioma',
// 							resizable: false,
// 							flex: 1,
// 							minWidth: 100,
// 						},

// 						{
// 							field: 'authors',
// 							headerName: 'Autor(es)',
// 							resizable: true,
// 							flex: 2,
// 							minWidth: 240,
// 						},

// 						{
// 							col_def: {
// 								field: 'buttons',
// 								headerName: '',
// 								pinned: 'right',
// 								cellStyle: CellStyle.Centered,
// 								width: 180,
// 							},

// 							renderer: (props: {
// 								data: {
// 									num_id: string;
// 									title: string;
// 								};
// 							}) => (
// 								<ButtonsContainer>
// 									<RemoveButton
// 										{...{
// 											style: {
// 												marginLeft: '.5rem',
// 											},

// 											on_confirm: () => remove.do_remove(props.data.num_id),

// 											title: `Deseja remover o livro ${props.data.title}?`,
// 										}}
// 									/>
// 								</ButtonsContainer>
// 							),
// 						},
// 					],
// 				}}
// 			/>
// 		</>
// 	);
// };
